import type { FC } from "react";

import Xarrow, { Xwrapper } from "react-xarrows";

import type { ExecutionFlowNode } from "../../types";
import type { EnrichedTicketNode } from "./ExecutionFlowGraph";

import { SectionHeader } from "../common/SectionHeader";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";
import { TicketCard } from "./TicketCard";

interface ExecutionTicketDetailsProps {
  mainTicket: ExecutionFlowNode | null;
  nodesByLevel: Map<number, EnrichedTicketNode[]>;
  onSelectNode: (node: ExecutionFlowNode) => void;
  className?: string;
}

/** Stable DOM id prefix to avoid collisions with other elements on the page. */
const CARD_ID_PREFIX = "dep-card-";

function cardDomId(planItemId: string): string {
  return `${CARD_ID_PREFIX}${planItemId}`;
}

const TicketLevelSection: FC<{
  group: EnrichedTicketNode[];
  level: number;
  onSelectNode: (node: ExecutionFlowNode) => void;
}> = ({ group, level, onSelectNode }) => (
  <div>
    <div className="mb-1.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
      <span className="whitespace-nowrap">
        Level {level} ({group.length})
      </span>
      <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
    </div>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {group.map((enrichedNode) => (
        <div
          key={enrichedNode.node.id}
          id={cardDomId(enrichedNode.node.plan_item_id)}
        >
          <TicketCard
            enrichedNode={enrichedNode}
            onClick={() => onSelectNode(enrichedNode.node)}
          />
        </div>
      ))}
    </div>
  </div>
);

export const ExecutionTicketDetails: FC<ExecutionTicketDetailsProps> = ({
  mainTicket,
  nodesByLevel,
  onSelectNode,
  className,
}) => {
  const sortedLevels = [...nodesByLevel.keys()].toSorted((a, b) => a - b);
  const totalCount = [...nodesByLevel.values()].reduce(
    (sum, group) => sum + group.length,
    0,
  );

  // Collect all dependency edges for arrows
  const depEdges: { from: string; to: string }[] = [];
  for (const group of nodesByLevel.values()) {
    for (const enriched of group) {
      for (const depId of enriched.dependencyIds) {
        depEdges.push({ from: depId, to: enriched.node.plan_item_id });
      }
    }
  }

  return (
    <div
      className={
        className ??
        "border-b border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900"
      }
    >
      <SectionHeader
        title="Ticket Details"
        icon={
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
            />
          </svg>
        }
      />

      {mainTicket && (
        <div className="mb-4 rounded-none border border-slate-300 bg-slate-50 p-3 shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-900/70 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="mb-1 flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-900 dark:text-slate-100">
                  {mainTicket.ticket_id || mainTicket.plan_item_id}
                </span>
                <StatusBadge status={mainTicket.status} size="sm" />
              </div>
              {mainTicket.summary && (
                <p className="line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
                  {mainTicket.summary}
                </p>
              )}
            </div>
            <Text
              as="div"
              className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
            >
              Main Ticket
            </Text>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <SectionHeader
          title="Subtickets"
          variant="label"
          className="mb-0"
          badge={String(totalCount)}
        />
        <div className="custom-scrollbar max-h-96 overflow-y-auto pr-1">
          <Xwrapper>
            <div className="relative space-y-3">
              {sortedLevels.map((level) => {
                const group = nodesByLevel.get(level);
                if (!group) {
                  return null;
                }
                return (
                  <TicketLevelSection
                    key={level}
                    group={group}
                    level={level}
                    onSelectNode={onSelectNode}
                  />
                );
              })}

              {/* Dependency arrows */}
              {depEdges.map(({ from, to }) => (
                <Xarrow
                  key={`${from}->${to}`}
                  start={cardDomId(from)}
                  end={cardDomId(to)}
                  startAnchor="bottom"
                  endAnchor="top"
                  color="#9ca3af"
                  strokeWidth={1.5}
                  headSize={4}
                  path="smooth"
                  curveness={0.4}
                  showHead
                  showTail={false}
                  zIndex={0}
                  passProps={{ opacity: 0.45, pointerEvents: "none" }}
                  SVGcanvasProps={{ className: "pointer-events-none" }}
                  divContainerStyle={{ pointerEvents: "none" }}
                />
              ))}
            </div>
          </Xwrapper>

          {nodesByLevel.size === 0 && (
            <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No subtickets found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
